import { useTravelCashFundBalanceQuery } from './useTravelCashFundBalance.graphql.generated';
import type { TravelCashFundBestCoupon } from './useTravelCashFundBalance.types';

type UseTravelCashFundBalanceArgs = Readonly<{
  eventId: string | undefined;
  /** Skip for users/surfaces that can never show fund UI (non-treatment, non-couple). */
  skip: boolean;
}>;

type TravelCashFundBalanceResult = Readonly<{
  /** Spendable balance (wallet + cash fund) in minor units; undefined = not an owner / no fund / error (fail closed). */
  totalSpendableMinorUnits: number | undefined;
  /** Wallet (travel credit) spendable balance in minor units; undefined when unavailable (fail closed). */
  travelCreditMinorUnits: number | undefined;
  /** Stripe cash-fund spendable balance in minor units; undefined when unavailable (fail closed). */
  cashFundMinorUnits: number | undefined;
  /** Fund currency (ISO 4217); undefined when the balance is unavailable. */
  fundCurrency: string | undefined;
  /**
   * The best cash-fund codes for this couple, at most one per payment rule, sorted by
   * `discountValue` descending — so the first entry is the headline.
   *
   * Advisory only: no booking exists yet, so nothing here is priced. Empty when there are no such
   * codes, when the caller may not read the balance, or when the coupon service could not be
   * reached — the balance fields still answer in every case, so a surface must degrade to copy that
   * quotes no discount rather than hiding the fund.
   */
  bestCashFundCoupons: ReadonlyArray<TravelCashFundBestCoupon>;
  isLoading: boolean;
  /** Re-reads the balance; call after a cancel so the page shows the post-refund figures. */
  refetch: () => Promise<unknown>;
}>;

/** Nothing to offer. Module-level so the identity is stable across renders for a `useMemo` dep. */
const NO_COUPONS: ReadonlyArray<TravelCashFundBestCoupon> = [];

/**
 * Real travel cash-fund balance for the logged-in couple. The query is owner-only:
 * every field is null for guests, events without a fund, or gateway errors, so
 * callers must treat `undefined` as "no fund" and fail closed.
 */
export const useTravelCashFundBalance = ({ eventId, skip }: UseTravelCashFundBalanceArgs): TravelCashFundBalanceResult => {
  const { data, loading, refetch } = useTravelCashFundBalanceQuery({
    batchMode: 'fast',
    variables: { eventId: eventId ?? '' },
    skip: skip || !eventId,
    // The balance is mutable (debited on booking, restored on cancel) and gates payment
    // UI — never decide on a cached value: fetch fresh on every mount.
    fetchPolicy: 'network-only',
  });
  const balance = data?.travelCashFundBalance;

  return {
    totalSpendableMinorUnits: balance?.totalSpendableMinorUnits ?? undefined,
    travelCreditMinorUnits: balance?.walletBalanceMinorUnits ?? undefined,
    cashFundMinorUnits: balance?.cashFundBalanceMinorUnits ?? undefined,
    fundCurrency: balance?.currency ?? undefined,
    bestCashFundCoupons: balance?.bestCashFundCoupons ?? NO_COUPONS,
    isLoading: loading,
    refetch,
  };
};
